import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import './Toast.css';

export type ToastTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export interface ToastInit {
  title?: ReactNode;
  message?: ReactNode;
  tone?: ToastTone;
  /** Auto-dismiss after this many ms. Default 4000. 0 = never. */
  duration?: number;
}

interface ToastEntry extends ToastInit {
  id: string;
  closing?: boolean;
}

interface ToastContextValue {
  toast: (init: ToastInit) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Wrap the app (or any subtree) in <ToastProvider> to enable useToast().
 * Renders a single positioned region that stacks toasts bottom-up.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timers = useRef(new Map<string, number>());

  const dismiss = useCallback((id: string) => {
    setToasts((list) => list.map((t) => (t.id === id ? { ...t, closing: true } : t)));
    const t = window.setTimeout(() => {
      setToasts((list) => list.filter((x) => x.id !== id));
    }, 220);
    timers.current.set(`${id}:close`, t);
  }, []);

  const toast = useCallback(
    (init: ToastInit) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((list) => [...list, { id, tone: 'neutral', duration: 4000, ...init }]);
      const duration = init.duration ?? 4000;
      if (duration > 0) {
        const t = window.setTimeout(() => dismiss(id), duration);
        timers.current.set(id, t);
      }
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const current = timers.current;
    return () => {
      current.forEach((t) => window.clearTimeout(t));
      current.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastRegion toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}

function ToastRegion({
  toasts,
  onDismiss,
}: {
  toasts: ToastEntry[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div className="fluent-toast-region" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <div
          key={t.id}
          className="fluent-toast"
          data-tone={t.tone}
          data-closing={t.closing || undefined}
          role={t.tone === 'danger' ? 'alert' : 'status'}
          aria-live={t.tone === 'danger' ? 'assertive' : 'polite'}
        >
          <span className="fluent-toast__icon" aria-hidden="true">
            <ToneIcon tone={t.tone ?? 'neutral'} />
          </span>
          <div className="fluent-toast__body">
            {t.title != null && <p className="fluent-toast__title">{t.title}</p>}
            {t.message != null && <p className="fluent-toast__message">{t.message}</p>}
          </div>
          <button type="button" className="fluent-toast__close" onClick={() => onDismiss(t.id)} aria-label="დახურე">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}

function ToneIcon({ tone }: { tone: ToastTone }) {
  switch (tone) {
    case 'success':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      );
    case 'warning':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 3l10 18H2L12 3zm0 6v5m0 2.5v.5" stroke="var(--color-surface)" strokeWidth="2" fill="currentColor" />
        </svg>
      );
    case 'danger':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v6" />
          <path d="M12 16.5v.5" />
        </svg>
      );
    case 'info':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v6" />
          <path d="M12 7v.5" />
        </svg>
      );
    case 'neutral':
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
  }
}
